import React, { useEffect, useMemo, useRef, useState } from 'react';
import Chart from 'chart.js/auto';
import 'chartjs-adapter-date-fns';
import zoomPlugin from 'chartjs-plugin-zoom';
import { IoIosWarning } from "react-icons/io";
import { IoIosInformationCircle } from "react-icons/io";
import MoonLoader from "react-spinners/MoonLoader";

Chart.register(zoomPlugin);

const DynamicLineChart = ({
    value,
    valuePropertyName,
    comparisonValue,
    unit,
    lowerThreshold,
    upperThreshold,
    height = 160,
    isEnlarged = false,
    selectedYearEditing,
    selectedAggregate,
    selectedRange, // array of [startDate, endDate]
    selectedTimestampToEditFromLineGraph,
    setSelectedTimestampToEditFromLineGraph,
    multuGroupSelectedArray,
    setSelectedYearEditing,
    availableDates,
    numericTimeAggregationMethod,
    longstandingGeoJSONRef,
    IsLoadingNewGeoJSONFromAnUpdate
}) => {
    const canvasRef = useRef(null);
    const chartRef = useRef(null);
    // const [panLevel, setPanLevel] = useState(0);

    const savedChartViewRef = useRef({
        min: null,
        max: null
    });

    useEffect(() => {
        return () => {
            chartRef.current?.destroy();
        };
    }, []);

    const aggregateDateUTC = (date, method) => {
        const d = new Date(date);

        switch (method) {
            case "year":
                return new Date(Date.UTC(
                    d.getUTCFullYear(),
                    0,
                    1
                ));

            case "month":
                return new Date(Date.UTC(
                    d.getUTCFullYear(),
                    d.getUTCMonth(),
                    1
                ));

            case "day":
                return new Date(Date.UTC(
                    d.getUTCFullYear(),
                    d.getUTCMonth(),
                    d.getUTCDate()
                ));

            case "hour":
                return new Date(Date.UTC(
                    d.getUTCFullYear(),
                    d.getUTCMonth(),
                    d.getUTCDate(),
                    d.getUTCHours()
                ));

            case "minute":
                return new Date(Date.UTC(
                    d.getUTCFullYear(),
                    d.getUTCMonth(),
                    d.getUTCDate(),
                    d.getUTCHours(),
                    d.getUTCMinutes()
                ));

            default:
                return d;
        }
    };

    const aggregateEntries = (entries, numericTimeAggregationMethod) => {
        if (!entries.length) return [];

        const buckets = new Map();

        entries.forEach(entry => {
            const bucketDate = aggregateDateUTC(
                entry.x,
                numericTimeAggregationMethod
            );

            const key = bucketDate.getTime();

            if (!buckets.has(key)) {
                buckets.set(key, {
                    date: bucketDate,
                    total: 0,
                    count: 0,
                });
            }

            const bucket = buckets.get(key);

            bucket.total += entry.y;
            bucket.count += 1;
        });

        return Array.from(buckets.values())
            .map(bucket => ({
                x: bucket.date,
                y: bucket.total / bucket.count,
            }))
            .sort((a, b) => a.x - b.x);
    };

    const aggregateAvailableDates = (dates, numericTimeAggregationMethod) => {
        if (!dates?.length) return [];

        const buckets = new Map();

        const getBucketKey = (date) => {
            const d = new Date(date);

            switch (numericTimeAggregationMethod) {
                case 'year':
                    return Date.UTC(
                        d.getUTCFullYear(),
                        0,
                        1
                    );

                case 'month':
                    return Date.UTC(
                        d.getUTCFullYear(),
                        d.getUTCMonth(),
                        1
                    );

                case 'day':
                    return Date.UTC(
                        d.getUTCFullYear(),
                        d.getUTCMonth(),
                        d.getUTCDate()
                    );

                case 'hour':
                    return Date.UTC(
                        d.getUTCFullYear(),
                        d.getUTCMonth(),
                        d.getUTCDate(),
                        d.getUTCHours()
                    );

                case 'minute':
                    return Date.UTC(
                        d.getUTCFullYear(),
                        d.getUTCMonth(),
                        d.getUTCDate(),
                        d.getUTCHours(),
                        d.getUTCMinutes()
                    );

                default:
                    return d.getTime();
            }
        };

        dates.forEach(date => {
            const key = getBucketKey(date);
            buckets.set(key, new Date(key));
        });

        return Array.from(buckets.values())
            .sort((a, b) => a - b)
            .map(date => ({
                x: date,
                y: null,
            }));
    };

    const rawEntries = useMemo(() => {
        if (!value || typeof value !== 'object') return [];
        return Object.entries(value)
            .map(([ts, v]) => ({
                x: new Date(ts),
                y: typeof v === 'number' ? v : null,
            }))
            .filter(d => d.y !== null)
            .sort((a, b) => a.x - b.x);
    }, [value]);

    const originalRawEntries = useMemo(() => {
        if (!comparisonValue || typeof comparisonValue !== 'object') return [];
        return Object.entries(comparisonValue)
            .map(([ts, v]) => ({
                x: new Date(ts),
                y: typeof v === 'number' ? v : null,
            }))
            .filter(d => d.y !== null)
            .sort((a, b) => a.x - b.x);
    }, [comparisonValue]);

    const aggregatedEntries = useMemo(() => {
        return aggregateEntries(rawEntries, numericTimeAggregationMethod);
    }, [rawEntries, numericTimeAggregationMethod]);

    const originalAggregatedEntries = useMemo(() => {
        return aggregateEntries(originalRawEntries, numericTimeAggregationMethod);
    }, [originalRawEntries, numericTimeAggregationMethod]);

    const aggregatedAvailableDates = useMemo(() => {
        const metricTimestamps = new Set(
            aggregatedEntries.map(d => d.x.getTime())
        );
        const aggregatedAvailableDatesResult = aggregateAvailableDates(
            availableDates,
            numericTimeAggregationMethod
        )
            .filter(d => !metricTimestamps.has(d.x.getTime()))
            .map(d => ({
                x: d.x,
                y: 0,
            }));
        return aggregatedAvailableDatesResult;
    }, [availableDates, numericTimeAggregationMethod, aggregatedEntries]);

    // check whether the current metric timeseries contains the selected timestamp.

    const metricMissingAtSelectedTimestamp = useMemo(() => {
        if (!selectedYearEditing || !value) {
            return false;
        }
        const targetTime = new Date(selectedYearEditing).getTime();
        return !Object.entries(value).some(([ts, val]) =>
            new Date(ts).getTime() === targetTime &&
            val !== undefined &&
            val !== null &&
            val !== ""
        );
    }, [
        selectedYearEditing,
        value
    ]);

    const windowSizes = {
        minute: 60,
        hour: 48,
        day: 30,
        month: 12,
        year: 10,
    };

    useEffect(() => {
        if (!canvasRef.current || !aggregatedEntries.length) return;

        if (chartRef.current) {
            const xScale = chartRef.current.scales?.x;

            if (xScale) {
                savedChartViewRef.current = {
                    min: xScale.min,
                    max: xScale.max
                };
            }

            chartRef.current.destroy();
        }

        const pointRadius = isEnlarged ? 4 : 3;
        const pointHoverRadius = isEnlarged ? 6 : 3;
        const pointHitRadius = isEnlarged ? 12 : 6;

        const datasets = [
            {
                label: 'Updated',
                data: aggregatedEntries,
                borderWidth: 2,
                tension: 0.25,
                pointRadius,
                pointHoverRadius,
                pointHitRadius,
                borderColor: '#29645D',
                backgroundColor: 'rgba(41,100,93,0.15)',
                fill: true,
            },
        ];

        if (originalAggregatedEntries.length) {
            datasets.push({
                label: 'Original',
                data: originalAggregatedEntries,
                borderWidth: 2,
                tension: 0.25,
                pointRadius,
                pointHoverRadius,
                pointHitRadius,
                borderColor: '#808080',
                backgroundColor: 'rgba(128,128,128,0.15)',
                fill: false,
            });
        }

        if (aggregatedAvailableDates.length) {
            datasets.push({
                label: 'Available Dates',
                data: aggregatedAvailableDates,
                borderWidth: 1,
                pointRadius: 4,
                pointHoverRadius: 6,
                pointBackgroundColor: 'transparent',
                pointBorderColor: '#bbb',
                showLine: false,
            });
        }

        if (typeof lowerThreshold === 'number') {
            datasets.push({
                label: 'Lower Threshold',
                data: aggregatedEntries.map(d => ({
                    x: d.x,
                    y: lowerThreshold,
                })),
                borderWidth: 1.5,
                borderDash: [6, 6],
                pointRadius: 0,
                borderColor: '#B42318',
                fill: false,
            });
        }

        if (typeof upperThreshold === 'number') {
            datasets.push({
                label: 'Upper Threshold',
                data: aggregatedEntries.map(d => ({
                    x: d.x,
                    y: upperThreshold,
                })),
                borderWidth: 1.5,
                borderDash: [6, 6],
                pointRadius: 0,
                borderColor: '#B42318',
                fill: false,
            });
        }

        const verticalLinePlugin = {
            id: 'verticalLinePlugin',
            afterDraw: (chart) => {
                if (!selectedYearEditing) return;

                const ctx = chart.ctx;
                const xScale = chart.scales.x;
                const yScale = chart.scales.y;
                const xPos = xScale.getPixelForValue(new Date(selectedYearEditing));

                ctx.save();
                ctx.beginPath();
                ctx.moveTo(xPos, yScale.top);
                ctx.lineTo(xPos, yScale.bottom);
                ctx.lineWidth = 2;
                ctx.strokeStyle = '#29645D';
                ctx.setLineDash([5, 5]);
                ctx.stroke();
                ctx.restore();
            },
        };

        const toIsoWithOffset = (date) => {
            const d = new Date(date);

            const pad = (n) => String(n).padStart(2, '0');

            const year = d.getUTCFullYear();
            const month = pad(d.getUTCMonth() + 1);
            const day = pad(d.getUTCDate());
            const hours = pad(d.getUTCHours());
            const minutes = pad(d.getUTCMinutes());
            const seconds = pad(d.getUTCSeconds());

            return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}+00:00`;
        };

        chartRef.current = new Chart(canvasRef.current, {
            type: 'line',
            data: { datasets },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                animation: false
                ,
                onClick: (event, elements) => {
                    if (!elements.length) return;
                    const chart = chartRef.current;
                    const first = elements[0];
                    const datasetIndex = first.datasetIndex;
                    const index = first.index;
                    const clickedPoint = chart.data.datasets[datasetIndex].data[index];
                    if (clickedPoint?.x) {
                        const timestamp = toIsoWithOffset(clickedPoint.x);
                        setSelectedYearEditing(timestamp);
                        console.log(timestamp);
                    }
                },
                plugins: {
                    legend: { display: false },

                    zoom: {
                        pan: {
                            enabled: true,
                            mode: 'x',
                        },
                        zoom: {
                            wheel: {
                                enabled: true,
                            },
                            pinch: {
                                enabled: true,
                            },
                            mode: 'x',
                        },
                    },
                },
                scales: {
                    x: {
                        type: 'time',
                        time: {
                            minUnit: 'second',
                            tooltipFormat: 'yyyy-MM-dd HH:mm:ss',
                            displayFormats: {
                                second: 'HH:mm:ss',
                                minute: 'HH:mm',
                                hour: 'dd MMM HH:mm',
                                day: 'dd MMM',
                                month: 'MMM yyyy',
                                year: 'yyyy',
                            },
                        },
                        ticks: {
                            font: { size: 8 },
                            autoSkip: true,
                        },
                    },
                    y: {
                        ticks: {
                            font: { size: 8 },
                            callback: function (value) {
                                return Number(value).toFixed(2);
                            },
                        },
                        beginAtZero: false,
                    },
                },
            },
            plugins: [verticalLinePlugin],
        });

        const savedView = savedChartViewRef.current;

        if (
            savedView.min !== null &&
            savedView.max !== null
        ) {
            chartRef.current.options.scales.x.min = savedView.min;
            chartRef.current.options.scales.x.max = savedView.max;
            chartRef.current.update('none');
        }

        return () => chartRef.current?.destroy();
    }, [
        aggregatedEntries,
        originalAggregatedEntries,
        aggregatedAvailableDates,
        numericTimeAggregationMethod,
        unit,
        upperThreshold,
        lowerThreshold,
        selectedYearEditing,
        isEnlarged,
    ]);

    const canPan = aggregatedEntries.length > windowSizes[numericTimeAggregationMethod];

    useEffect(() => {
        if (!chartRef.current || aggregatedEntries.length === 0) return;
        const chart = chartRef.current;
        if (selectedRange && selectedRange.length === 2) {
            const [start, end] = selectedRange;
            chart.options.scales.x.min = start.getTime();
            chart.options.scales.x.max = end.getTime();
        } else {
            chart.options.scales.x.min = undefined;
            chart.options.scales.x.max = undefined;
        }
        chart.update('none');
    }, [selectedRange, aggregatedEntries, originalAggregatedEntries, selectedYearEditing]);

    // useEffect(() => {
    //     if (!aggregatedEntries.length) return;
    //     const windowSize = windowSizes[numericTimeAggregationMethod];
    //     if (aggregatedEntries.length <= windowSize) {
    //         setPanLevel(0);
    //     } else {
    //         setPanLevel(100);
    //     }
    // }, [aggregatedEntries, numericTimeAggregationMethod]);

    // useEffect(() => {
    //     if (!chartRef.current || !aggregatedEntries.length) return;
    //     const chart = chartRef.current;
    //     let startTime;
    //     let endTime;
    //     if (selectedRange && selectedRange.length === 2) {
    //         startTime = selectedRange[0].getTime();
    //         endTime = selectedRange[1].getTime();
    //     } else {
    //         startTime = aggregatedEntries[0].x.getTime();
    //         endTime = aggregatedEntries[aggregatedEntries.length - 1].x.getTime();
    //     }
    //     const totalRange = endTime - startTime;
    //     const visibleRange = totalRange * (zoomLevel / 100);
    //     const maxPan = totalRange - visibleRange;
    //     const offset = (panLevel / 100) * maxPan;
    //     chart.options.scales.x.min = startTime + offset;
    //     chart.options.scales.x.max = startTime + offset + visibleRange;
    //     chart.update('none');
    // }, [
    //     aggregatedEntries,
    //     selectedRange,
    //     zoomLevel,
    //     panLevel,
    // ]);

    // explicitly set the chart x-axis to the raw data range

    // useEffect(() => {
    //     if (!chartRef.current || !aggregatedEntries.length) return;
    //     const chart = chartRef.current;
    //     const startTime = rawEntries[0].x.getTime();
    //     const endTime = rawEntries[rawEntries.length - 1].x.getTime();
    //     chart.options.scales.x.min = startTime;
    //     chart.options.scales.x.max = endTime;
    //     chart.update('none');
    //     setZoomLevel(100);
    //     setPanLevel(0);
    // }, [
    //     numericTimeAggregationMethod,
    //     aggregatedEntries,
    //     rawEntries
    // ]);

    useEffect(() => {
        if (!chartRef.current || !aggregatedEntries.length) return;

        const savedView = savedChartViewRef.current;

        if (
            savedView.min === null ||
            savedView.max === null
        ) {
            return;
        }

        chartRef.current.options.scales.x.min = savedView.min;
        chartRef.current.options.scales.x.max = savedView.max;

        chartRef.current.update('none');
    }, [
        aggregatedEntries,
        originalAggregatedEntries,
        aggregatedAvailableDates,
        numericTimeAggregationMethod,
        unit,
        upperThreshold,
        lowerThreshold,
        isEnlarged
    ]);

    return (
        <div style={{
            paddingTop: '0px',
            height: metricMissingAtSelectedTimestamp ? '150px' : '90px',
            paddingBottom: '50px'
        }}>
            {IsLoadingNewGeoJSONFromAnUpdate && (
                <div
                    style={{
                        left: '45%',
                        top: '45%',
                        position: 'relative'
                    }}>
                    <MoonLoader
                        size={30}
                        color="#29645D"
                    />
                </div>
            )}
            {metricMissingAtSelectedTimestamp && !IsLoadingNewGeoJSONFromAnUpdate && (
                <div
                    title="No data for this Value at this timestamp"
                    style={{
                        marginLeft: '0px',
                        textAlign: 'left',
                        zIndex: 10,
                        color: '#fff',
                        fontSize: '12px',
                        backgroundColor: '#989898',
                        paddingLeft: '10px',
                        marginLeft: '10px',
                        paddingTop: '10px',
                        paddingBottom: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        width: 'calc(100% - 30px)',
                        borderRadius: '3px',
                        marginBottom: '10px'
                    }}
                >
                    <IoIosInformationCircle
                        color="#fff"
                        size={20}
                        style={{ flexShrink: 0 }}
                    />

                    <span style={{
                        textAlign: 'left'
                    }}>
                        No <b>{valuePropertyName}</b> data for selected<br />features at {' '}
                        <b>
                            {new Date(selectedYearEditing).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric'
                            })}
                        </b>
                    </span>
                </div>
            )}
            <div style={{
                height: '135px',
                width: '100%',
                position: 'relative',
                display: IsLoadingNewGeoJSONFromAnUpdate ? 'none' : 'block'
            }}>
                <canvas ref={canvasRef} />
            </div>
        </div>
    );
};

export default DynamicLineChart;